'use client'

import BodyText from '@lyra/core/components/BodyText'
import Icon, { ArrowUpRight, ChevronRight } from '@lyra/core/components/Icon'
import Spinner from '@lyra/core/components/Spinner'
import Table, { createColumnHelper, TableData, TableProps } from '@lyra/core/components/Table'
import Tag from '@lyra/core/components/Tag'
import Tooltip from '@lyra/core/components/Tooltip'
import filterNulls from '@lyra/core/utils/filterNulls'
import formatNumber from '@lyra/core/utils/formatNumber'
import formatPercentage from '@lyra/core/utils/formatPercentage'
import formatTruncatedUSD from '@lyra/core/utils/formatTruncatedUSD'
import TokenIcon from '@lyra/web/components/common/TokenIcon'
import { MARKET_ICON_SIZE, SECTION_SPINNER_SIZE } from '@lyra/web/constants/layout'
import { PageId } from '@lyra/web/constants/pages'
import { PARTNER_VAULTS, PartnerVaultId } from '@lyra/web/constants/partnerVaults'
import { CollateralId } from '@lyra/web/constants/tokens'
import { YieldTokenId } from '@lyra/web/constants/yield'
import { yieldCopy } from '@lyra/web/constants/yieldCopy'
import { usePartnerVaultStats } from '@lyra/web/hooks/usePartnerVaultStats'
import useYieldAllStats from '@lyra/web/hooks/useYieldAllStats'
import useYieldPositions from '@lyra/web/hooks/useYieldPositions'
import { getPagePath } from '@lyra/web/utils/pages'
import { formatCollateralBalance, formatTokenSymbol } from '@lyra/web/utils/tokens'
import { getYieldTokenConfig } from '@lyra/web/utils/yield'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import React, { useMemo } from 'react'
import { XStack, YStack } from 'tamagui'

import { YieldIsExecutingTag } from '../yield/YieldIsExecutingTag'
import { YieldRiskTag } from '../yield/YieldRiskTag'
import YieldStackYStack from '../yield/YieldStackYStack'
import { YieldStrategyTag } from '../yield/YieldStrategyTag'

type YieldData = TableData<
  (
    | {
        id: YieldTokenId
        isExternal: false
      }
    | {
        id: PartnerVaultId
        isExternal: true
      }
  ) & {
    isFeatured: boolean
    name: string
    tvl: number
    totalSupply: number
    apy: number
    collateralId: CollateralId
    balanceValue: number
    balance: number
  }
>

type Props = Omit<TableProps<YieldData>, 'data' | 'columns'>

export default function YieldTable({ ...tableProps }: Props) {
  const { data: allStats, isLoading: isStatsLoading } = useYieldAllStats()

  const { data: partnerStats, isLoading: isPartnerStatsLoading } = usePartnerVaultStats()

  const { data: positions, isLoading: isPositionsLoading } = useYieldPositions()

  const isLoading = isStatsLoading || isPositionsLoading || isPartnerStatsLoading

  const router = useRouter()

  const data: YieldData[] = useMemo(() => {
    if (!allStats || !partnerStats) {
      return []
    }

    const vaultRows: YieldData[] = Object.values(allStats).map(({ id }) => {
      const stats = allStats[id]
      const config = getYieldTokenConfig(id)
      const copy = yieldCopy[id]

      const apy = (stats.baseApy ?? 0) + (stats.optionsApy ?? 0)

      const position = positions ? positions[id] : null

      return {
        id,
        isFeatured: config.isFeatured ?? false,
        isExternal: false,
        name: copy.name,
        tvl: stats?.tvl ?? 0,
        totalSupply: stats ? stats.totalSupply : 0,
        apy,
        collateralId: config.collateralId,
        balanceValue: position ? position.value : 0,
        balance: position ? position.balance : 0,
        onPress: () => router.push(getPagePath({ page: PageId.Vault, id: config.id })),
        hoverBg: yieldCopy[config.id].backgroundColor,
      }
    })

    const partnerVaultRows: YieldData[] = Object.values(partnerStats).map(
      ({ id, tvl, totalSupply, balance }) => {
        const config = PARTNER_VAULTS[id]
        return {
          id,
          isFeatured: false,
          isExternal: true,
          name: config.name,
          tvl,
          totalSupply,
          apy: 0,
          collateralId: config.collateralId,
          balanceValue: balance,
          balance,
          onPress: () => window.open(config.url, '_blank'),
        }
      }
    )

    return [...partnerVaultRows, ...vaultRows].sort((a, b) => {
      // vaults with balances go first
      const balanceDiff = b.balanceValue - a.balanceValue
      if (balanceDiff) {
        return balanceDiff
      }
      // featured vaults go next
      const featuredDiff = (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0)
      if (featuredDiff) {
        return featuredDiff
      }
      // vaults with largest tvl go last
      return b.tvl - a.tvl
    })
  }, [allStats, partnerStats, positions, router])

  const showBalances = useMemo(() => data.some((dat) => dat.balance > 0), [data])

  const columnHelper = createColumnHelper<YieldData>()
  const columns = useMemo(() => {
    return filterNulls([
      columnHelper.accessor('name', {
        header: 'Vault',
        meta: {
          minWidth: 180,
        },
        cell: (props) => {
          const { id, collateralId, isExternal } = props.row.original
          return (
            <XStack gap="$1" alignItems="center">
              {isExternal ? (
                <Image
                  width={MARKET_ICON_SIZE}
                  height={MARKET_ICON_SIZE}
                  src={PARTNER_VAULTS[id].imageUrl}
                  alt={id}
                />
              ) : (
                <TokenIcon size={MARKET_ICON_SIZE} symbol={collateralId} />
              )}
              <YStack>
                <BodyText>{props.getValue()}</BodyText>
                <BodyText color="secondary">{formatTokenSymbol(collateralId)}</BodyText>
              </YStack>
            </XStack>
          )
        },
      }),
      columnHelper.accessor('tvl', {
        header: 'TVL',
        meta: {
          minWidth: 120,
          isSortable: true,
        },
        cell: ({
          row: {
            original: { tvl, totalSupply, collateralId },
          },
        }) => {
          return (
            <YStack>
              <BodyText>{formatTruncatedUSD(tvl)}</BodyText>
              <BodyText color="secondary">
                {formatCollateralBalance(totalSupply, collateralId, { truncate: true })}
              </BodyText>
            </YStack>
          )
        },
      }),
      columnHelper.accessor('apy', {
        header: 'APY',
        meta: {
          minWidth: 190,
          isSortable: true,
        },
        cell: (props) => {
          const { id, isExternal } = props.row.original

          if (isExternal) {
            return <BodyText>{PARTNER_VAULTS[id].apyRange}</BodyText>
          }

          const config = getYieldTokenConfig(id)
          const copy = yieldCopy[id]

          const apyLabel =
            copy.yieldStack[0].isBase || copy.yieldStack[0].isOptions
              ? formatPercentage(props.row.original.apy, {
                  showSign: props.row.original.apy !== 0,
                  dps: 2,
                })
              : copy.yieldStack[0].yieldValue

          return (
            <Tooltip
              content={<YieldStackYStack width={250} gap="$1" yieldId={id} />}
              trigger={
                <XStack cursor="help" alignItems="center" gap="$1">
                  <BodyText
                    color={config.isEarlyDeposit || !props.row.original.apy ? 'primary' : 'green'}
                  >
                    {apyLabel}
                  </BodyText>
                  <BodyText>+</BodyText>
                  <Tag
                    label={copy.yieldStack
                      .filter((stack) => !stack.isBase && !stack.isOptions)
                      .map((stack, idx) => (
                        <XStack key={idx} borderRadius={10000} overflow="hidden">
                          <Icon size={18} icon={stack.icon} color={stack.iconColor} />
                        </XStack>
                      ))}
                  />
                </XStack>
              }
            />
          )
        },
      }),
      columnHelper.accessor('direction', {
        header: 'Strategy',
        meta: {
          minWidth: 180,
        },
        cell: (props) => {
          const { id, isExternal } = props.row.original
          if (isExternal) {
            const config = PARTNER_VAULTS[id]
            return (
              <XStack gap="$1" flexWrap="wrap" width="100%">
                <Tooltip
                  trigger={<Tag label={config.strategyTitle} cursor="help" />}
                  title={config.strategyTitle}
                  content={config.strategyDescription}
                />
                <Tooltip
                  trigger={<Tag label="External" cursor="help" />}
                  title="External"
                  content="This vault is provided by a third party. Derive is not responsible for the reliability, performance or actions taken by this vault provider."
                />
              </XStack>
            )
          }
          const config = getYieldTokenConfig(id)
          return (
            <XStack gap="$1" flexWrap="wrap" width="100%">
              <YieldStrategyTag yieldId={config.id} />
              {config.isEarlyDeposit ? (
                <YieldIsExecutingTag yieldId={config.id} />
              ) : config.strategyId === 'CoveredCallSpread' ||
                config.strategyId === 'CoveredPutSpread' ||
                config.strategyId === 'LongPrincipalProtected' ? (
                <YieldRiskTag yieldId={config.id} />
              ) : null}
            </XStack>
          )
        },
      }),
      showBalances
        ? columnHelper.accessor('balance', {
            header: 'Balance',
            meta: {
              minWidth: 100,
              isSortable: true,
            },
            cell: (props) => {
              const { balance, collateralId, balanceValue } = props.row.original

              if (!balance) {
                return <BodyText color="secondary">—</BodyText>
              }

              return (
                <YStack>
                  <BodyText color={balance ? 'primary' : 'secondary'}>
                    {formatNumber(balance)} {formatTokenSymbol(collateralId)}
                  </BodyText>
                  {balanceValue ? (
                    <BodyText color="secondary">{formatTruncatedUSD(balanceValue)}</BodyText>
                  ) : null}
                </YStack>
              )
            },
          })
        : null,
      columnHelper.accessor('chevron', {
        header: '',
        meta: {
          minWidth: 16,
          maxWidth: 16,
        },
        cell: (props) => {
          const { isExternal } = props.row.original
          return (
            <Icon
              icon={isExternal ? <ArrowUpRight /> : <ChevronRight />}
              size={16}
              color="secondary"
            />
          )
        },
      }),
    ])
  }, [columnHelper, showBalances])

  if (isLoading) {
    return (
      <XStack height={300} justifyContent="center" alignItems="center" {...tableProps}>
        <Spinner size={SECTION_SPINNER_SIZE} color="red" />
      </XStack>
    )
  }

  return (
    <Table
      size="lg"
      emptyMessage="Failed to load vaults"
      {...tableProps}
      columns={columns}
      data={data}
    />
  )
}
